import {
    Text,
    Heading,
    Colors,
    Card,
    Icon,
    stepRailProgressTransition,
    STEP_RAIL_LAYOUT,
    floatingBandShadow,
    stepRailProgressGradient,
} from '@amelio/ui-library';
import { useRef, useState } from 'react';

import { StepRailItem } from './components/StepRailItem';
import { StepItem } from './StepItem';

import type { StepperProps, SizeConfig } from './types';

import { useAutoScroll } from '@/hooks';
import {
    cycleRailSurfaceBlur,
    cycleRailSurfaceBorderColor,
    cycleRailSurfaceGradient,
} from '@/utils/cycleStyles';

export const Stepper = ({
    steps,
    currentStep,
    orientation = 'vertical',
    variant = 'card',
    size = 'md',
    title,
    subtitle,
    onStepClick,
    getStepAriaLabel,
    getStepRankLabel,
    className = '',
    containerClassName = '',
    navAriaLabel = 'Progress stepper',
    colors: colorOverrides,
    badgeAppearance,
    dataTestId,
}: StepperProps) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const titleColor = Colors.BASE;
    const subtitleColor = Colors.BASE;
    const activeLabelColor = Colors.BASE;
    const completedLabelColor = Colors.BASE;
    const descriptionColor = Colors.BASE_300;
    const completedConnectorColor = Colors.SECONDARY_700;

    const upcomingLabelColor = Colors.BASE_300;
    const rankColor = Colors.BASE_300;
    const upcomingConnectorColor = Colors.BASE_300;
    const isHorizontal = orientation === 'horizontal';

    const stepRefs = useRef<(HTMLElement | null)[]>([]);

    const activeIndex =
        typeof currentStep === 'number'
            ? currentStep
            : steps.findIndex((step) => step.id === currentStep);

    useAutoScroll(activeIndex, stepRefs);

    const getSizeConfig = (): SizeConfig => {
        switch (size) {
            case 'sm':
                return {
                    indicator: 'h-8 w-8',
                    numberText: 'h5' as const,
                    labelText: 'caption' as const,
                    descriptionText: 'caption' as const,
                    titleLevel: 6,
                    subtitleSize: 'small',
                    gap: 'gap-2',
                };
            case 'lg':
                return {
                    indicator: 'h-12 w-12',
                    numberText: 'h3' as const,
                    labelText: 'body' as const,
                    descriptionText: 'body-small' as const,
                    titleLevel: 4,
                    subtitleSize: 'large',
                    gap: 'gap-4',
                };
            case 'md':
            default:
                return {
                    indicator: 'h-10 w-10',
                    numberText: 'h4' as const,
                    labelText: 'body-small' as const,
                    descriptionText: 'caption' as const,
                    titleLevel: 5,
                    subtitleSize: 'default',
                    gap: 'gap-3',
                };
        }
    };

    const sizeConfig = getSizeConfig();

    // Spread last so a caller that names no colour gets exactly what the stepper has always drawn.
    const colors = {
        active: activeLabelColor,
        completed: completedLabelColor,
        upcoming: upcomingLabelColor,
        rank: rankColor,
        description: descriptionColor,
        completedConnector: completedConnectorColor,
        upcomingConnector: upcomingConnectorColor,
        ...colorOverrides,
    };

    const isStepClickable = (step: (typeof steps)[number]): boolean => {
        return !step.disabled && !!onStepClick;
    };

    const getStepStatus = ({
        step,
        stepIndex,
    }: {
        step: (typeof steps)[number];
        stepIndex: number;
    }) => {
        if (step.status) {
            return step.status;
        }
        if (stepIndex < activeIndex) {
            return 'completed' as const;
        } else if (stepIndex === activeIndex) {
            return 'active' as const;
        }
        return 'upcoming' as const;
    };

    const activeStep = steps[activeIndex];

    const halfColumnPct = steps.length > 0 ? 50 / steps.length : 0;
    const progressRatio = steps.length > 1 ? activeIndex / (steps.length - 1) : 0;

    if (variant === 'rail') {
        return (
            <Card
                className={`${STEP_RAIL_LAYOUT.bandPadding} ${floatingBandShadow} w-full ${cycleRailSurfaceBlur} ${containerClassName}`.trim()}
                dataTestId={dataTestId}
                borderRadius={'stepRailBand'}
                borderColor={cycleRailSurfaceBorderColor}
                bgColor={cycleRailSurfaceGradient}
                padding={'none'}
            >
                <nav aria-label={navAriaLabel}>
                    <div className={'relative'}>
                        <span
                            aria-hidden={'true'}
                            className={`${STEP_RAIL_LAYOUT.trackTop} ${STEP_RAIL_LAYOUT.trackThickness} bg-step-rail-track-unreached absolute rounded-full`}
                            style={{
                                left: `${halfColumnPct}%`,
                                right: `${halfColumnPct}%`,
                            }}
                        />

                        <span
                            aria-hidden={'true'}
                            className={`${STEP_RAIL_LAYOUT.trackTop} ${STEP_RAIL_LAYOUT.trackThickness} ${stepRailProgressGradient} absolute rounded-full ${stepRailProgressTransition}`}
                            style={{
                                left: `${halfColumnPct}%`,
                                width: `calc((100% - ${halfColumnPct * 2}%) * ${progressRatio})`,
                            }}
                        />

                        <ol
                            className={`scrollbar-hide ${STEP_RAIL_LAYOUT.headroom} ${STEP_RAIL_LAYOUT.columnGap} relative flex items-start overflow-x-auto overflow-y-visible ${className}`}
                        >
                            {steps.map((step, index) => (
                                <StepRailItem
                                    key={step.id}
                                    step={step}
                                    index={index}
                                    activeIndex={activeIndex}
                                    size={size}
                                    isClickable={isStepClickable(step)}
                                    rankLabel={getStepRankLabel?.(index)}
                                    onStepClick={onStepClick}
                                    getStepAriaLabel={getStepAriaLabel}
                                    stepRef={(el) => {
                                        stepRefs.current[index] = el;
                                    }}
                                />
                            ))}
                        </ol>
                    </div>
                </nav>
            </Card>
        );
    }

    return (
        <Card
            className={`${
                isHorizontal
                    ? 'w-fit max-w-full px-10 py-8'
                    : `flex h-fit max-h-full !w-full flex-col px-8 pb-8 lg:max-w-xs ${title || subtitle ? 'pt-8' : 'pt-12'}`
            } ${containerClassName}`}
            dataTestId={dataTestId}
            borderRadius={'2xl'}
            borderColor={Colors.BASE_50}
        >
            <div className={''}>
                {(title || subtitle) && (
                    <div className={'mb-5 overflow-hidden lg:mb-12'}>
                        {title && (
                            <Heading
                                level={sizeConfig.titleLevel}
                                gutterBottom
                                color={titleColor}
                                className={'mb-2 text-wrap lg:mb-4'}
                            >
                                {title}
                            </Heading>
                        )}

                        {subtitle && (
                            <Text
                                size={sizeConfig.subtitleSize}
                                color={subtitleColor}
                                className={'text-wrap'}
                            >
                                {subtitle}
                            </Text>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile header - collapses the column of steps, which only a vertical stepper has:
                a horizontal one is one row tall on any screen and scrolls sideways on its own. */}
            {!isHorizontal && (
                <div className={'block p-1 lg:!hidden'}>
                    <div
                        className={
                            'border-base-200 mb-4 flex items-center justify-between rounded-lg border bg-white p-4'
                        }
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    >
                        <div className={'flex items-center gap-3'}>
                            <div
                                className={`bg-primary-700 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-xl font-bold text-white`}
                            >
                                <span className={'text-sm font-medium text-white'}>
                                    {activeIndex + 1}
                                </span>
                            </div>

                            <div className={'flex flex-col'}>
                                <span className={'text-base-900 mb-2 text-base font-medium'}>
                                    {activeStep?.label}
                                </span>

                                <span className={'text-base-400 text-sm'}>
                                    {`Step ${activeIndex + 1} of ${steps.length}`}
                                </span>
                            </div>
                        </div>

                        <Icon
                            name={'arrow-arrow-down'}
                            size={20}
                            className={`text-base-400 h-5 w-5 ${isCollapsed ? '' : 'rotate-180'}`}
                        />
                    </div>
                </div>
            )}

            <div
                className={`scrollbar-hide flex ${isHorizontal ? 'flex-row items-stretch overflow-x-auto pb-4' : 'min-h-0 flex-1 flex-col overflow-y-auto pt-2 pb-12 lg:px-4'} ${sizeConfig.gap} ${className} ${isCollapsed ? 'hidden' : 'mt-4'}`}
                role={'navigation'}
                aria-label={navAriaLabel}
            >
                {steps.map((step, index) => (
                    <StepItem
                        key={step.id}
                        step={step}
                        index={index}
                        activeIndex={activeIndex}
                        size={size}
                        isHorizontal={isHorizontal}
                        isLast={index === steps.length - 1}
                        isClickable={isStepClickable(step)}
                        sizeConfig={sizeConfig}
                        colors={colors}
                        badgeAppearance={badgeAppearance}
                        nextStepStatus={
                            index < steps.length - 1
                                ? getStepStatus({
                                      step: steps[index + 1],
                                      stepIndex: index + 1,
                                  })
                                : undefined
                        }
                        onStepClick={onStepClick}
                        getStepAriaLabel={getStepAriaLabel}
                        stepRef={(el) => {
                            stepRefs.current[index] = el;
                        }}
                    />
                ))}
            </div>
        </Card>
    );
};
